import React, { useEffect, useMemo, useState } from "react";
import useBrowseState from "../hooks/useBrowseState";
import { sameBrowseView } from "../utils/browseState";
import "leaflet/dist/leaflet.css";
import "../styles/pages/BrowsePhotographersPage.css";

import UniversalSearchBar from "../components/browse_photographers_page/UniversalSearchBar";
import DateFilter from "../components/browse_photographers_page/DateFilter";
import PhotographerGrid from "../components/browse_photographers_page/PhotographerGrid";
import SpecialityFilter from "../components/browse_photographers_page/SpecialityFilter";
import PhotographerMap from "../components/browse_photographers_page/PhotographerMap";

const PAGE_SIZE = 24;

export default function BrowsePhotographersPage() {
  const { state, view, viewport, locationValue, onLocationChange, commit, onViewChange, markInteraction, onMapInteraction } = useBrowseState();
  const { date: selectedDate, speciality: selectedFilter, includeUnavailable } = state;
  const [photographers, setPhotographers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, has_more: false, offset: 0 });
  const [paging, setPaging] = useState({ key: null, offset: 0 });
  const [attempt, setAttempt] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [hoveredSub, setHoveredSub] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [isMapExpanded, setIsMapExpanded] = useState(false);

  const query = useMemo(() => {
    if (!viewport || !sameBrowseView(viewport.view, view)) return null;
    const params = new URLSearchParams();
    if (selectedFilter !== "All") params.set("speciality", selectedFilter);
    if (selectedDate) {
      params.set("date", selectedDate);
      params.set("include_unavailable", String(includeUnavailable));
    }
    Object.entries(viewport.bounds).forEach(([key, value]) => params.set(key, String(value)));
    return params.toString();
  }, [viewport, view, selectedFilter, selectedDate, includeUnavailable]);
  const offset = paging.key === query ? paging.offset : 0;

  useEffect(() => {
    setPaging({ key: query, offset: 0 });
    setSelectedSub(null);
    setHoveredSub(null);
  }, [query]);

  useEffect(() => {
    setIsLoading(true);
    setLoadError("");
    if (!query) return;
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      const params = new URLSearchParams(query);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      try {
        const response = await fetch(
          `https://xg2bkmyfg1.execute-api.eu-west-2.amazonaws.com/dev/photographers?${params}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error(`Browse API returned ${response.status}`);
        const data = await response.json();
        const items = Array.isArray(data) ? data : data?.items;
        if (!Array.isArray(items)) throw new Error("Invalid browse response");
        const meta = Array.isArray(data)
          ? { total: data.length, has_more: false, offset: 0 }
          : data.pagination || { total: items.length, has_more: false, offset };
        if (controller.signal.aborted) return;
        setPhotographers((current) => offset === 0 ? items : [...current, ...items]);
        setPagination(meta);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Error fetching photographers:", error);
        if (offset === 0) setPhotographers([]);
        setLoadError("We couldn't load photographers. Please try again.");
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }, 250);
    return () => { clearTimeout(timeout); controller.abort(); };
  }, [query, offset, attempt]);

  const setSelectedDate = (date) => commit({ date });
  const setSelectedFilter = (speciality) => commit({ speciality });
  const setIncludeUnavailable = (value) => commit({ includeUnavailable: value });

  const handleHoverIn = (sub) => setHoveredSub(sub);
  const handleHoverOut = () => setHoveredSub(null);
  const handleSelect = (sub) => {
    setSelectedSub((current) => (current === sub ? null : sub));
  };
  const loadMorePhotographers = () => {
    if (loadError) setAttempt((value) => value + 1);
    else setPaging({ key: query, offset: photographers.length });
  };

  const handleLocationSelect = (suggestion) => {
    const feature = suggestion.data;
    if (feature.bbox) {
      const [minLng, minLat, maxLng, maxLat] = feature.bbox;
      commit({ where: suggestion.label, view: { center: [(minLat + maxLat) / 2, (minLng + maxLng) / 2], zoom: 10 } });
    } else if (feature.center) {
      const [lng, lat] = feature.center;
      commit({ where: suggestion.label, view: { center: [lat, lng], zoom: 12 } });
    }
  };

  const clearFilters = () => commit({ speciality: "All", date: "", includeUnavailable: false, where: "" });

  const hasActiveFilters = selectedFilter !== "All" || Boolean(selectedDate) || Boolean(locationValue);

  return (
    <div className={`browse-photographers-page ${isMapExpanded ? "map-expanded" : ""}`}>
      <div className="main-content">
        <div className={`left-section controls-section ${isMapExpanded ? "hidden" : ""}`}>
          <div className="browse-intent-row">
            <UniversalSearchBar
              value={locationValue}
              onChange={onLocationChange}
              onSelect={handleLocationSelect}
            />
            <DateFilter value={selectedDate} onChange={setSelectedDate} />
            <SpecialityFilter
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
            />
          </div>

          <div className="controls-row">
            {selectedDate && (
              <button
                type="button"
                className="active-filter-chip"
                onClick={() => setSelectedDate("")}
              >
                {selectedDate}
                <span aria-hidden="true">×</span>
              </button>
            )}

            {selectedFilter !== "All" && (
              <button
                type="button"
                className="active-filter-chip"
                onClick={() => setSelectedFilter("All")}
              >
                {selectedFilter}
                <span aria-hidden="true">×</span>
              </button>
            )}

            {selectedDate && (
              <label className="availability-toggle">
                <input
                  type="checkbox"
                  checked={includeUnavailable}
                  onChange={(event) => setIncludeUnavailable(event.target.checked)}
                />
                <span>Show unavailable photographers</span>
              </label>
            )}

            {hasActiveFilters && (
              <button type="button" className="clear-filters-button" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>

        <div className={`results-section ${isMapExpanded ? "hidden" : ""}`}>
          <div className="results-toolbar">
            <div>
              <strong>{photographers.length}</strong>
              <span> of {pagination.total} photographers</span>
              {selectedFilter !== "All" && (
                <span className="results-context"> matching {selectedFilter.toLowerCase()}</span>
              )}
            </div>
          </div>

          {loadError && photographers.length === 0 ? (
            <div className="browse-state">
              <p>{loadError}</p>
              <button
                type="button"
                onClick={() => setAttempt((value) => value + 1)}
              >
                Try again
              </button>
            </div>
          ) : photographers.length === 0 && isLoading ? (
            <div className="browse-state">Loading photographers…</div>
          ) : photographers.length === 0 ? (
            <div className="browse-state">
              <p>No photographers match these filters yet.</p>
              {hasActiveFilters && (
                <button type="button" onClick={clearFilters}>Clear filters</button>
              )}
            </div>
          ) : (
            <>
              <PhotographerGrid
                photographers={photographers}
                onHoverIn={handleHoverIn}
                onHoverOut={handleHoverOut}
                onSelect={handleSelect}
                selectedSub={selectedSub}
              />

              {pagination.has_more && (
                <div className="load-more-row">
                  <button
                    type="button"
                    className="load-more-button"
                    disabled={isLoading}
                    onClick={loadMorePhotographers}
                  >
                    {isLoading ? "Loading…" : "Load more photographers"}
                  </button>
                  <span>
                    Showing {photographers.length} of {pagination.total}
                  </span>
                  {loadError && <p role="alert">{loadError}</p>}
                </div>
              )}
            </>
          )}
        </div>

        <div className={`right-section ${isMapExpanded ? "full-width" : ""}`}>
          <div className="map-view">
            <button
              className="expand-map-button"
              onClick={() => setIsMapExpanded(!isMapExpanded)}
            >
              {isMapExpanded ? "Collapse Map" : "Expand Map"}
            </button>

            <PhotographerMap
              photographers={photographers}
              view={view}
              onViewChange={onViewChange}
              onInteraction={markInteraction}
              onMoveStart={onMapInteraction}
              hoveredSub={hoveredSub}
              selectedSub={selectedSub}
              isMapExpanded={isMapExpanded}
            />

            {pagination.total > 0 && (
              <div className="map-results-overlay">
                <span>
                  {photographers.length} of {pagination.total} shown
                </span>
                {pagination.has_more && (
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={loadMorePhotographers}
                  >
                    {isLoading ? "Loading…" : "Load more"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
